/**
 * 監査ログの内部コードを日本語にする（D-086）。
 *
 * 画面では `member.created` のような内部コードをそのまま出しており、
 * 事務局は「何の記録か」も「何で絞り込めるか」も分からなかった。
 * ここに無いコードは、隠さずコードのまま表示する（記録を取りこぼさない）。
 */

/** 検索プルダウンに出す操作の分類（よく調べるものを業務の言葉でまとめる） */
export interface AuditActionGroup {
  label: string;
  /** この分類に含まれる操作コードの前方一致（APIには1つずつ渡す） */
  action: string;
}

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  'auth.login.succeeded': 'ログイン成功',
  'auth.login.failed': 'ログイン失敗',
  'auth.login.rate_limited': 'ログイン試行の回数超過（自動で一時停止）',
  'auth.logout': 'ログアウト',
  'auth.password.changed': 'パスワード変更（本人）',
  'auth.password.reset_requested': 'パスワード再設定メールの送信',
  'auth.password.reset_completed': 'パスワード再設定の完了',
  'auth.password.reset_by_office': '一時パスワードの発行（事務局）',
  'auth.totp.enrolled': '2要素認証の設定',
  'auth.totp.verified': '2要素認証の確認',
  'auth.totp.failed': '2要素認証の失敗',
  'auth.backup_code.used': 'バックアップコードの使用',
  'auth.active_role.switched': 'ロールの切替',

  'invitation.created': '招待の発行',
  'invitation.accepted': '招待の受諾',
  'invitation.revoked': '招待の取り消し',
  'user.role.granted': '権限の付与',
  'user.role.revoked': '権限の削除',
  'user.deactivated': 'アカウントの停止',
  'user.activated': 'アカウントの再開',
  'user.profile.updated': 'プロフィールの変更',
  'staff.active_changed': '担当者の有効・停止の切替',
  'office_account.grade_changed': '事務局の権限区分の変更',
  'office_account.status_changed': '事務局アカウントの有効・停止',

  'member.created': '会員の登録',
  'member.updated': '会員情報の変更',
  'member.status_changed': '在籍状態の変更',
  'assignment.created': '担当の割当',
  'assignment.deleted': '担当の解除',
  'member_meet_email.changed': 'Meet用メールの変更',

  'cohort.created': '期の追加',
  'cohort.updated': '期の変更',
  'cohort.active_changed': '期の有効・無効の切替',
  'event_type.created': 'イベント種別の追加',
  'event_type.updated': 'イベント種別の変更',
  'event_type.active_changed': 'イベント種別の有効・無効の切替',

  'event.created': 'イベントの作成',
  'event.copied': 'イベントのコピー',
  'event.updated': 'イベントの変更',
  'event.status_changed': 'イベントの状態変更',
  'event.published': 'イベントの公開',
  'event.target_set': 'イベント対象者の設定',
  'attendance.updated': '出欠の変更',
  'absence_request.created': '欠席申請の提出',
  'absence_request.decided': '欠席申請の承認・却下',
  'seating.confirmed': '席次の確定',
  'seating.published': '席次の公開',
  'seating_assignment.changed': '席次の個別変更',

  'score.published': '採点結果の公開',
  'score.rolled_back': '採点結果の公開取り消し',
  'csv.confirmed': 'CSV取込の確定',
  'disclosure.changed': '開示範囲の変更',

  'president_survey_response.created': '社長アンケートの登録',
  'president_survey_response.updated': '社長アンケートの編集',
  'president_survey_response.deleted': '社長アンケートの削除',
  'president_survey_response.viewed': '社長アンケートの閲覧',

  'survey_template.created': 'アンケートテンプレートの作成',
  'survey_template_version.published': 'アンケートの版の公開',
  'event_survey.created': 'イベントへのアンケート割当',
  'survey_response.submitted': 'アンケートの提出',

  'thread.created': 'スレッドの作成',
  'message.posted': 'メッセージの投稿',
  'message.deleted': 'メッセージの削除',
  'inbox_item.assigned': '問い合わせの担当割当',
  'inbox_item.status_changed': '問い合わせの状態変更',

  'meet_attendance_log.imported': 'Meet参加ログの取込',
  'meet_attendance_log.resolved': 'Meet参加ログの手動確定',
  'drive_file.changed': 'Drive資料の変更',
  'ng_rule.created': '席次NGの登録',
  'ng_rule.status_changed': '席次NGの承認・却下',

  'event_photo.uploaded': '当日写真の登録',
  'event_photo.deleted': '当日写真の削除',
  'scheduling_slot.created': '面談枠の登録',
  'scheduling_slot.cancelled': '面談枠のキャンセル',
  'scheduling_slot.claimed': '面談枠の予約',
  'success_record.created': '面談記録の作成',
  'success_record.updated': '面談記録の変更',
  'follow_up.created': 'フォローアップの登録',
  'follow_up.status_changed': 'フォローアップの状態変更',
  'one_on_one.assigned': '1on1のアサイン',

  'integration.oauth.changed': '外部連携の接続変更',
  'integration.settings.changed': '外部連携の設定変更',
  'notification_preset.updated': '通知プリセットの変更',
  'notification_template.updated': '通知テンプレートの変更',
  'notification.test_sent': '通知のテスト送信',
  'notification_schedule.cancelled': '通知予約のキャンセル',
  'notification_schedule.rescheduled': '通知予約の時刻変更',
  'notification_schedule.event_extra_created': 'この回だけの通知追加',
  'notification_schedule.manual_created': '手入力の通知の登録',

  'migration_job.created': 'データ移行ジョブの作成',
  'migration_job.dry_run_executed': 'データ移行のお試し実行',
  'migration_job.applied': 'データ移行の適用',
  'superadmin.maintenance_mode_changed': '保守モードの切替',
  'superadmin.job_retried': '配信の再実行',
};

/** 検索欄のプルダウン（よく調べるものだけを業務の言葉で並べる） */
export const AUDIT_ACTION_CHOICES: AuditActionGroup[] = [
  { label: '会員の登録', action: 'member.created' },
  { label: '会員情報の変更', action: 'member.updated' },
  { label: '在籍状態の変更', action: 'member.status_changed' },
  { label: '担当の割当', action: 'assignment.created' },
  { label: '担当の解除', action: 'assignment.deleted' },
  { label: 'イベントの公開', action: 'event.published' },
  { label: '出欠の変更', action: 'attendance.updated' },
  { label: '席次の確定', action: 'seating.confirmed' },
  { label: '採点結果の公開', action: 'score.published' },
  { label: '採点結果の公開取り消し', action: 'score.rolled_back' },
  { label: 'CSV取込の確定', action: 'csv.confirmed' },
  { label: '開示範囲の変更', action: 'disclosure.changed' },
  { label: '権限の付与', action: 'user.role.granted' },
  { label: '権限の削除', action: 'user.role.revoked' },
  { label: '一時パスワードの発行（事務局）', action: 'auth.password.reset_by_office' },
  { label: 'ログイン失敗', action: 'auth.login.failed' },
  { label: '外部連携の設定変更', action: 'integration.settings.changed' },
  { label: 'データ移行の適用', action: 'migration_job.applied' },
];

/** 一覧・検索で使う表示名。未知のコードはコードのまま返す */
export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

export const AUDIT_RESOURCE_LABELS: Record<string, string> = {
  user: 'アカウント',
  invitation: '招待',
  member: '会員',
  member_meet_email: 'Meet用メール',
  staff: '担当者',
  survey_template_version: 'アンケートの版',
  survey_answer: 'アンケートの回答',
  seating_assignment: '席次の割当',
  subcommittee_feedback: '分科会のフィードバック',
  waitlist: 'キャンセル待ち',
  notification_schedule: '通知の予約',
  notification: '通知',
  event: 'イベント',
  attendance: '出欠',
  absence_request: '欠席申請',
  assignment: '担当',
  cohort: '期',
  event_type: 'イベント種別',
  survey_template: 'アンケートテンプレート',
  survey_response: 'アンケート回答',
  event_survey: 'イベントのアンケート',
  score: '採点',
  seating: '席次',
  thread: 'スレッド',
  message: 'メッセージ',
  inbox_item: '問い合わせ',
  session: 'ログイン状態',
  office_auth: '2要素認証',
  office_account: '事務局アカウント',
  integration: '外部連携',
  migration_job: 'データ移行',
  csv_job: 'CSV取込',
  notification_preset: '通知プリセット',
  notification_template: '通知テンプレート',
  success_record: '面談記録',
  follow_up: 'フォローアップ',
  scheduling_slot: '面談枠',
  ng_rule: '席次NG',
  drive_file: 'Drive資料',
  meet_attendance_log: 'Meet参加ログ',
  president_survey_response: '社長アンケート回答',
};

export function auditResourceLabel(resourceType: string): string {
  return AUDIT_RESOURCE_LABELS[resourceType] ?? resourceType;
}
